import React, {useState} from "react"
import {useTranslation} from "react-i18next"
import {useController, useFormContext} from "react-hook-form"
import moment, {Moment} from "moment"
import 'moment/locale/ru';

import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import {DatePicker} from "@mui/x-date-pickers-pro"

import {COLORS} from "../../../../../../../../../../../../../config/constants"

import {ENGINE_TYPE_OPTIONS, PACKAGE_FORM, PackageForm} from "../add_form/helpers"

import './vehicle_fields.scss';

const ELECTRIC = 'electric'

const VehicleFields = () => {

  const { t } = useTranslation()

  const { control, setValue, trigger } = useFormContext<PackageForm>()

  const { field: engineTypeField } = useController({
    name: PACKAGE_FORM.ENGINE_TYPE,
    control,
  })

  const { field: enginePowerField } = useController({
    name: PACKAGE_FORM.ENGINE_POWER_HP,
    control,
  })

  const { field: engineVolumeField } = useController({
    name: PACKAGE_FORM.ENGINE_VOLUME_CC,
    control,
  })

  const { field: manufactureDateField } = useController({
    name: PACKAGE_FORM.MANUFACTURE_DATE,
    control,
    defaultValue: '',
  })

  const engineType = engineTypeField.value ?? ''
  const manufactureDate = manufactureDateField.value ? moment(manufactureDateField.value) : null

  const [ dateError, setDateError ] = useState(false)

  const renderEngineTypeValue = (value: string) => {
    const option = ENGINE_TYPE_OPTIONS.find((item) => item.value === value)

    if (!option) {
      return ''
    }

    return t(option.labelKey)
  }

  const handleChangeEngineType = (event: SelectChangeEvent) => {
    const value = event.target.value

    if (value === ELECTRIC) {
      engineVolumeField.onChange(undefined)
      setValue(PACKAGE_FORM.ENGINE_VOLUME_CC, undefined, { shouldDirty: true })
    }
    engineTypeField.onChange(value)
    trigger(PACKAGE_FORM.PALLETES)
  }

  const handleChangeDate = (value: Moment | null) => {
    const formattedDate = moment(value).format('YYYY-MM-DD')
    const isInvalid = formattedDate === 'Invalid date' || moment().diff(moment(value)) < 0
    const nextValue = value && !isInvalid ? formattedDate : ''

    setDateError(Boolean(value) && isInvalid)
    manufactureDateField.onChange(nextValue)
    setValue(PACKAGE_FORM.MANUFACTURE_DATE, nextValue || undefined, { shouldDirty: true })
    trigger(PACKAGE_FORM.PALLETES)
  }

  const handleChangeNumber = (
    field: typeof enginePowerField | typeof engineVolumeField,
    formField: PACKAGE_FORM.ENGINE_POWER_HP | PACKAGE_FORM.ENGINE_VOLUME_CC,
  ) => ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    const numberValue = Number(value)
    const nextValue = value !== '' && numberValue > 0 ? numberValue : undefined

    field.onChange(nextValue)
    setValue(formField, nextValue, { shouldDirty: true })
    trigger(PACKAGE_FORM.PALLETES)
  }

  return (
    <div className='vehicle-fields'>
      <Typography variant='subtitle2' sx={{ fontSize: 16, marginBottom: 2 }}>
        {t('calc__text__packaging__vehicleParams')}
      </Typography>
      <div className='vehicle-fields__row'>
        <FormControl fullWidth>
          <InputLabel id='engineType-label' shrink sx={{ background: COLORS.WHITE }}>
            {t('calc__text__packaging__engineType')}
          </InputLabel>
          <Select
            labelId='engineType-label'
            id='engineType'
            label={t('calc__text__packaging__engineType')}
            value={engineType}
            displayEmpty
            onChange={handleChangeEngineType}
            renderValue={renderEngineTypeValue}
          >
            {ENGINE_TYPE_OPTIONS.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant='outlined'
          type='number'
          label={t('calc__text__packaging__enginePower')}
          value={enginePowerField.value ?? ''}
          onChange={handleChangeNumber(enginePowerField, PACKAGE_FORM.ENGINE_POWER_HP)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 1 }}
        />
      </div>
      <div className='vehicle-fields__row'>
        {engineType !== ELECTRIC && (
          <TextField
            variant='outlined'
            type='number'
            label={t('calc__text__packaging__engineVolume')}
            value={engineVolumeField.value ?? ''}
            onChange={handleChangeNumber(engineVolumeField, PACKAGE_FORM.ENGINE_VOLUME_CC)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1 }}
          />
        )}
        <DatePicker
          label={t('calc__text__packaging__manufactureDate')}
          value={manufactureDate}
          disableFuture
          onChange={handleChangeDate}
          sx={{
            '.MuiPickersOutlinedInput-root': {
              borderRadius: 0,
            },
          }}
          slotProps={{
            textField: {
              error: dateError,
              helperText: dateError ? t('errors__message__incorrectDate') : '',
              InputLabelProps: { shrink: true },
            },
          }}
        />
      </div>
    </div>
  )
}

export default VehicleFields
